import * as React from 'react';
import { useMemo } from 'react';

import GradeIcon from '@/components/elements/Icon/Grade';
import TimeFrameIcon from '@/components/elements/Icon/TimeFrame';
import {
  GoodFormRacer,
  Race,
  RaceProgram,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { getVelodromeByCode, velodromes } from '@/lib/race/velodrome';
import { toHalfWidth } from '@/utility/utils';

import {
  CenterBox,
  Container,
  Content,
  DeadLine,
  IconArea,
  LeftBox,
  RaceInfo,
  RacerData,
  RacerName,
  ResultLink,
  RightBox,
  RightBoxButton,
  Times,
  VelodromeName,
} from './style';

interface GoodRacerProps {
  mode: 'WinStreak' | 'WinAverage';
  goodFormRacer: GoodFormRacer;
  raceProgram?: RaceProgram;
  race?: Race;
}

const GoodRacer: React.FC<GoodRacerProps> = ({
  mode,
  goodFormRacer,
  raceProgram,
  race,
}) => {
  if (!raceProgram || !race) {
    return <></>;
  }

  const velodrome = useMemo(
    () => getVelodromeByCode(raceProgram.velodromeCode),
    [raceProgram],
  );

  const isAfterDeadline = useMemo(() => {
    if (race) {
      return dayjst().isAfter(race.orderDeadline);
    }
    return false;
  }, [race]);

  const hrefLink = useMemo(() => {
    if (raceProgram && race) {
      const targetVelodrome = velodromes.filter(
        item => item.code == raceProgram.velodromeCode,
      )[0];

      const baseLink = `${
        targetVelodrome.romanName
      }/race/${raceProgram.heldDate.format('YYYY-MM-DD')}/${race.raceNum}`;
      if (isAfterDeadline) {
        return baseLink + '?infoType=result';
      }
      return baseLink;
    }
    return '';
  }, [race, raceProgram]);

  return (
    <Container>
      <Content>
        <RaceInfo to={hrefLink}>
          <LeftBox>
            <RacerName>{goodFormRacer.name}</RacerName>
            <RacerData>
              {toHalfWidth(goodFormRacer.gradeName)}
              {toHalfWidth(goodFormRacer.unitName)}/
              {goodFormRacer.averageRaceScore.toFixed(2)}
            </RacerData>
          </LeftBox>
          <CenterBox>
            {mode === 'WinStreak' ? (
              <span>{goodFormRacer.winningStreakCount}連勝中</span>
            ) : (
              <span>勝率{goodFormRacer.winningAverage.toFixed(0)}％</span>
            )}
          </CenterBox>
          {isAfterDeadline ? (
            <RightBoxButton>
              <ResultLink>結果</ResultLink>
            </RightBoxButton>
          ) : (
            <RightBox>
              <VelodromeName>
                {velodrome.name}
                <IconArea>
                  <GradeIcon gradeName={raceProgram.gradeName} />
                </IconArea>
                <IconArea>
                  <TimeFrameIcon timeFrame={raceProgram.timeFrame} />
                </IconArea>
              </VelodromeName>
              <Times>
                {race.raceNum}R
                <DeadLine>
                  締切
                  <time>{race.orderDeadline.format('HH:mm')}</time>
                </DeadLine>
              </Times>
            </RightBox>
          )}
        </RaceInfo>
      </Content>
    </Container>
  );
};

export default GoodRacer;
